import React, { useEffect, useRef, useState } from 'react';

// "The record that should already exist".
// Five records that were gathered for other reasons. When the section comes into view
// they scatter outward from the board's question, then keep drifting while their lines
// reach towards the question and fall short. Nothing ever connects.
const W = 640, H = 400;
const SLOT = { x: 220, y: 158, w: 200, h: 84 };
const SC = { x: SLOT.x + SLOT.w / 2, y: SLOT.y + SLOT.h / 2 };
const NAVY = '#07152D', CY = '#00A6DA', INK = '#5B6878';

const CARDS = [
  { id: 'psychometric', x: 28, y: 26, w: 170, h: 92, rot: -4, title: 'Psychometric', sub: 'from a hire four years ago', href: '/diagnostics', aria: 'Leadership assessment', ax: 14, ay: 10, ar: 2.2, p: 7.5, depth: 14 },
  { id: '360', x: 428, y: 22, w: 186, h: 84, rot: 3, title: '360 feedback', sub: 'from a leadership programme', href: '/diagnostics', aria: '360 feedback', ax: 12, ay: 12, ar: 2.0, p: 9.0, depth: 10 },
  { id: 'engagement', x: 470, y: 262, w: 150, h: 74, rot: -3, title: 'Engagement survey', sub: null, href: '/culturepulse', aria: 'CulturePulse culture measure', ax: 15, ay: 9, ar: 2.5, p: 8.2, depth: 16 },
  { id: 'appraisal', x: 40, y: 276, w: 150, h: 74, rot: 4, title: 'Appraisal rating', sub: null, href: '/leadership-succession-intelligence', aria: 'Succession', ax: 13, ay: 11, ar: 2.4, p: 10.0, depth: 12 },
  { id: 'devplan', x: 238, y: 304, w: 172, h: 80, rot: -2, title: 'Development plan', sub: 'nobody opened again', href: '/coaching', aria: 'Coaching and leadership development', ax: 10, ay: 8, ar: 1.6, p: 6.8, depth: 8 },
];

const ease = (t) => 1 - Math.pow(1 - Math.min(Math.max(t, 0), 1), 3);
const edge = (px, py, ux, uy, hw, hh) => {
  const t = Math.min(ux ? hw / Math.abs(ux) : 1e9, uy ? hh / Math.abs(uy) : 1e9);
  return [px + ux * t, py + uy * t];
};

export const FragmentCards = () => {
  const svgRef = useRef(null);
  const cardRefs = useRef([]);
  const lineRefs = useRef([]);
  const tipRefs = useRef([]);
  const slotRef = useRef(null);
  const hover = useRef(-1);
  const mouse = useRef({ x: 0, y: 0 });
  const [, setReduced] = useState(false);

  useEffect(() => {
    const rm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setReduced(rm);
    const svg = svgRef.current;

    const place = (now, shown) => {
      const s = now / 1000;
      CARDS.forEach((c, i) => {
        const e = shown == null ? 1 : ease((s - shown - i * 0.09) / 1.3);
        const cx0 = c.x + c.w / 2, cy0 = c.y + c.h / 2;
        const ph = i * 1.7;
        const dx = rm ? 0 : c.ax * Math.sin((2 * Math.PI * s) / c.p + ph) + mouse.current.x * c.depth;
        const dy = rm ? 0 : c.ay * Math.cos((2 * Math.PI * s) / (c.p * 1.13) + ph) + mouse.current.y * c.depth - (hover.current === i ? 6 : 0);
        const rot = c.rot + (rm ? 0 : c.ar * Math.sin((2 * Math.PI * s) / (c.p * 1.4) + ph));
        const px = SC.x + (cx0 + dx - SC.x) * e, py = SC.y + (cy0 + dy - SC.y) * e;
        const sc = 0.55 + 0.45 * e;
        const g = cardRefs.current[i];
        if (g) {
          g.setAttribute('transform', `translate(${px.toFixed(2)} ${py.toFixed(2)}) rotate(${(rot * e).toFixed(2)}) scale(${sc.toFixed(3)}) translate(${-c.w / 2} ${-c.h / 2})`);
          g.style.opacity = e.toFixed(3);
        }
        const ddx = SC.x - px, ddy = SC.y - py, d = Math.hypot(ddx, ddy) || 1, ux = ddx / d, uy = ddy / d;
        const a = edge(px, py, ux, uy, (c.w * sc) / 2 + 6, (c.h * sc) / 2 + 6);
        const b0 = edge(SC.x, SC.y, -ux, -uy, SLOT.w / 2 + 10, SLOT.h / 2 + 10);
        const L = Math.max(0, Math.hypot(b0[0] - a[0], b0[1] - a[1]));
        const reach = rm ? 0.6 : 0.3 + 0.62 * (0.5 + 0.5 * Math.sin((2 * Math.PI * s) / 3.4 + i * 1.3));
        const r = reach * e;
        const b = [a[0] + ux * L * r, a[1] + uy * L * r];
        const ln = lineRefs.current[i], tp = tipRefs.current[i];
        if (ln) { ln.setAttribute('x1', a[0].toFixed(1)); ln.setAttribute('y1', a[1].toFixed(1)); ln.setAttribute('x2', b[0].toFixed(1)); ln.setAttribute('y2', b[1].toFixed(1)); ln.setAttribute('stroke-dashoffset', rm ? 0 : (-(s * 14) % 16).toFixed(2)); }
        if (tp) { tp.setAttribute('cx', b[0].toFixed(1)); tp.setAttribute('cy', b[1].toFixed(1)); tp.setAttribute('opacity', (0.35 + 0.6 * reach).toFixed(2)); tp.setAttribute('r', (2 + 1.6 * reach).toFixed(2)); }
      });
      if (slotRef.current && !rm) slotRef.current.setAttribute('stroke-dashoffset', (-(s * 6) % 12).toFixed(2));
    };

    if (rm) { place(0, null); return undefined; }

    let shown = null, raf;
    const io = new IntersectionObserver(([en]) => {
      if (en.isIntersecting && shown == null) shown = performance.now() / 1000;
    }, { threshold: 0.35 });
    io.observe(svg);
    const onMove = (ev) => {
      const r = svg.getBoundingClientRect();
      mouse.current = { x: ((ev.clientX - r.left) / r.width - 0.5) * 2, y: ((ev.clientY - r.top) / r.height - 0.5) * 2 };
    };
    const onLeave = () => { mouse.current = { x: 0, y: 0 }; };
    svg.addEventListener('mousemove', onMove); svg.addEventListener('mouseleave', onLeave);
    const tick = (now) => { place(now, shown == null ? 1e9 : shown); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); io.disconnect(); svg.removeEventListener('mousemove', onMove); svg.removeEventListener('mouseleave', onLeave); };
  }, []);

  return (
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} role="group" aria-label="Five records gathered for other reasons, none of which answers the board's question"
      style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}>
      <defs>
        <pattern id="fc-grid" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="1" cy="1" r="1" fill="#C9D6E3" /></pattern>
        <filter id="fc-sh" x="-20%" y="-20%" width="140%" height="170%"><feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#07152D" floodOpacity=".12" /></filter>
      </defs>
      <rect width={W} height={H} rx="16" fill="#F3FAFD" />
      <rect width={W} height={H} rx="16" fill="url(#fc-grid)" opacity=".7" />
      {CARDS.map((c, i) => (
        <g key={`l${c.id}`}>
          <line ref={(el) => { lineRefs.current[i] = el; }} stroke={CY} strokeWidth="1.5" strokeDasharray="3 5" strokeLinecap="round" opacity=".8" />
          <circle ref={(el) => { tipRefs.current[i] = el; }} r="2.6" fill={CY} />
        </g>
      ))}
      <a href="/leadership-succession-intelligence" aria-label="Succession">
        <rect ref={slotRef} x={SLOT.x} y={SLOT.y} width={SLOT.w} height={SLOT.h} rx="10" fill="#FFFFFF" fillOpacity=".7" stroke={NAVY} strokeOpacity=".4" strokeWidth="1.3" strokeDasharray="6 6" />
        <text x={SC.x} y={SC.y - 8} textAnchor="middle" fontFamily="Poppins, sans-serif" fontSize="9" letterSpacing="2" fontWeight="600" fill={CY}>THE BOARD ASKS</text>
        <text x={SC.x} y={SC.y + 10} textAnchor="middle" fontFamily="Poppins, sans-serif" fontSize="12" fontWeight="600" fill={NAVY}>Who could run the</text>
        <text x={SC.x} y={SC.y + 26} textAnchor="middle" fontFamily="Poppins, sans-serif" fontSize="12" fontWeight="600" fill={NAVY}>East Africa business?</text>
      </a>
      {CARDS.map((c, i) => (
        <a key={c.id} href={c.href} aria-label={c.aria} style={{ cursor: 'pointer' }}
          onMouseEnter={() => { hover.current = i; }} onMouseLeave={() => { hover.current = -1; }}>
          <g ref={(el) => { cardRefs.current[i] = el; }} filter="url(#fc-sh)"
            transform={`translate(${c.x} ${c.y})`}>
            <rect width={c.w} height={c.h} rx="8" fill="#FFFFFF" stroke="#DCE4ED" />
            <rect width="4" height={c.h} rx="2" fill={CY} />
            <text x="16" y="24" fontFamily="Poppins, sans-serif" fontSize="12" fontWeight="600" fill={NAVY}>{c.title}</text>
            {c.sub && <text x="16" y="40" fontFamily="Poppins, sans-serif" fontSize="9.5" fill={INK}>{c.sub}</text>}
            <rect x="16" y={c.sub ? 54 : 40} width={(c.w - 32) * 0.8} height="3.2" rx="1.6" fill="#E3E9F0" />
            <rect x="16" y={c.sub ? 63 : 49} width={(c.w - 32) * 0.55} height="3.2" rx="1.6" fill="#E3E9F0" />
          </g>
        </a>
      ))}
    </svg>
  );
};

export default FragmentCards;
